import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { handleNetworkError } from '../../utils/api/networkErrors';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  image_url: string;
  category: string;
  status: 'draft' | 'published';
  published_at?: string;
}

export async function getBlogPosts() {
  try {
    const { data, error } = await supabase
      .from('blog_posts').select(`
        *,
        user_profiles:author_id (
          first_name,
          last_name,
          avatar_url
        )
      `).order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error loading blog posts:', error);
    handleNetworkError(error);
    throw error;
  }
}

export async function createBlogPost(postData: Omit<BlogPost, 'id'>) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Authentication required');

    const { data, error } = await supabase
      .from('blog_posts')
      .insert({
        ...postData,
        author_id: user.id,
        published_at: postData.status === 'published' ? new Date().toISOString() : null
      })
      .select()
      .single();

    if (error) throw error;
    toast.success('Blog post created successfully');
    return data;
  } catch (error) {
    handleNetworkError(error);
    throw error;
  }
}

export async function updateBlogPost(postId: string, postData: Partial<BlogPost>) {
  try {
    const updates = {
      ...postData,
      updated_at: new Date().toISOString(),
      published_at: postData.status === 'published' 
        ? new Date().toISOString() 
        : null
    };

    const { data, error } = await supabase
      .from('blog_posts')
      .update(updates)
      .eq('id', postId)
      .select()
      .single();

    if (error) throw error;
    toast.success('Blog post updated successfully');
    return data;
  } catch (error) {
    handleNetworkError(error);
    throw error;
  }
}

export async function deleteBlogPost(postId: string) {
  try {
    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', postId);

    if (error) throw error;
    toast.success('Blog post deleted successfully');
  } catch (error) {
    handleNetworkError(error);
    throw error;
  }
}